/**
 * OpenAI Whisper API Transcriber (Optimized for Speed)
 * Handles audio recording and transcription using Whisper-1 model
 * English-only support with optimizations for faster processing
 * 
 * Note: API key is NOT needed on client side
 * Transcription is handled by backend via /api/transcribe endpoint
 */

export class WhisperTranscriber {
  constructor(apiKey) {
    // apiKey parameter is deprecated and not used
    // Backend handles all OpenAI API communication
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.isRecording = false;
    this.mediaStream = null;
    this.recordingStartTime = null;
  }

  /**
   * Start recording audio from microphone with optimized settings
   */
  async startRecording() {
    try {
      this.audioChunks = [];
      this.recordingStartTime = Date.now();
      
      // Request audio stream with optimized constraints for faster processing
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 16000 } // Optimal for Whisper: 16kHz
        } 
      });
      
      // Use optimized MediaRecorder settings
      const options = {
        mimeType: 'audio/webm;codecs=opus', // Opus codec is faster and smaller
        audioBitsPerSecond: 128000 // Balanced quality/speed
      };
      
      // Fallback if codec not supported
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        this.mediaRecorder = new MediaRecorder(this.mediaStream);
      } else {
        this.mediaRecorder = new MediaRecorder(this.mediaStream, options);
      }

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.start(500); // Collect data every 500ms for faster processing
      this.isRecording = true;
      console.log('🎤 Recording started (16kHz, optimized)...');
      return true;
    } catch (error) {
      console.error('❌ Error accessing microphone:', error);
      throw new Error('Cannot access microphone. Please check browser permissions.');
    }
  }

  /**
   * Stop recording and return audio blob (optimized)
   */
  stopRecording() {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error('No recording in progress'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
        this.isRecording = false;
        
        // Clean up
        this.mediaStream.getTracks().forEach(track => track.stop());
        
        const recordingDuration = Date.now() - this.recordingStartTime;
        console.log(`⏹️ Recording stopped (${recordingDuration}ms, ${audioBlob.size} bytes)`);
        resolve(audioBlob);
      };

      this.mediaRecorder.stop();
    });
  }

  /**
   * Transcribe audio blob using backend API with optimizations
   * This avoids CORS issues by routing through the backend
   * @param {Blob} audioBlob - Audio file blob
   * @param {string} token - Authentication token for backend
   * @returns {Promise<string>} - Transcribed text
   */
  async transcribeAudio(audioBlob, token) {
    try {
      const startTime = Date.now();
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');

      console.log('📤 Sending audio to backend transcription API...');

      const response = await fetch('/api/transcribe', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || error.message || `Transcription failed with status ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Transcription failed');
      }
      
      const elapsed = Date.now() - startTime;
      console.log(`✅ Transcription successful (${elapsed}ms)`);
      console.log(`   Backend processing: ${data.processingTime}ms`);
      console.log(`   Network + overhead: ${elapsed - data.processingTime}ms`);
      
      return data.text;
    } catch (error) {
      console.error('❌ Transcription error:', error);
      throw error;
    }
  }

  /**
   * Full workflow: Record audio and transcribe it
   * @returns {Promise<string>} - Transcribed text
   */
  async recordAndTranscribe() {
    try {
      await this.startRecording();
      return await this.stopRecording().then(audioBlob => this.transcribeAudio(audioBlob));
    } catch (error) {
      console.error('❌ Record and transcribe error:', error);
      throw error;
    }
  }

  /**
   * Check if recording is active
   */
  isRecordingActive() {
    return this.isRecording;
  }

  /**
   * Abort recording
   */
  abortRecording() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.mediaStream?.getTracks().forEach(track => track.stop());
      this.isRecording = false;
      console.log('⏹️ Recording aborted');
    }
  }
}

export default WhisperTranscriber;
